// src/components/PostTripModal.jsx
import { useEffect, useRef, useState } from "react";
import { db } from "../services/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

// Simple signature pad (same style as PrestartModal)
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches && e.touches[0];
      const x = (t ? t.clientX : e.clientX) - rect.left;
      const y = (t ? t.clientY : e.clientY) - rect.top;
      return { x, y };
    };

    const down = (e) => {
      drawing.current = true;
      ctx.beginPath();
      const p = getPos(e);
      ctx.moveTo(p.x, p.y);
      e.preventDefault();
    };
    const move = (e) => {
      if (!drawing.current) return;
      const p = getPos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      e.preventDefault();
    };
    const up = () => {
      if (!drawing.current) return;
      drawing.current = false;
      onChange?.(canvas.toDataURL("image/png"));
    };

    canvas.addEventListener("mousedown", down);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);

    return () => {
      canvas.removeEventListener("mousedown", down);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={140}
        className="border rounded bg-white w-full"
      />
      <button
        type="button"
        onClick={clear}
        className="mt-2 text-xs px-2 py-1 rounded border"
      >
        Clear Signature
      </button>
    </div>
  );
}

export default function PostTripModal({
  driverId,
  date,
  onClose,
  onCompleted,
  postTripDocId,
  prestartDocId,
  driverEmail = "",
  runKey = "",
  companyId: assignedCompanyId = "",
  companyName: assignedCompanyName = "",
  vehicleId: assignedVehicleId = "",
  vehicleRego: assignedVehicleRego = "",
  vehicleLoadType: assignedVehicleLoadType = "",
  vehicleLabel: assignedVehicleLabel = "",
}) {
  const [odoEnd, setOdoEnd] = useState("");
  const [hourMeterEnd, setHourMeterEnd] = useState("");
  const [tripEndTime, setTripEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState(null);

  // Link back to truck / company if we can find them
  const [meta, setMeta] = useState({
    companyId: assignedCompanyId || null,
    companyName: assignedCompanyName || null,
    truckId: assignedVehicleId || null,
    registrationNo: assignedVehicleRego || null,
    unitType: assignedVehicleLoadType || null,
    vehicleLabel: assignedVehicleLabel || null,
  });

  // Load existing post trip (for editing) + metadata from prestart
  useEffect(() => {
    if (!driverId || !date) return;

    const load = async () => {
      const id = postTripDocId || prestartDocId || `${driverId}_${date}`;

      // 1) existing post trip (if any)
      const postRef = doc(db, "postTrips", id);
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        const d = postSnap.data();
        setOdoEnd(d.odometerEnd ? String(d.odometerEnd) : "");
        setHourMeterEnd(d.hourMeterEnd != null && d.hourMeterEnd !== "" ? String(d.hourMeterEnd) : "");
        setTripEndTime(d.tripEndTime || "");
        setNotes(d.notes || "");
        setSig(d.signature || null);
        setMeta({
          companyId: d.companyId || assignedCompanyId || null,
          companyName: d.companyName || assignedCompanyName || null,
          truckId: d.truckId || d.vehicleId || assignedVehicleId || null,
          registrationNo: d.registrationNo || d.vehicleRego || assignedVehicleRego || null,
          unitType: d.unitType || d.vehicleLoadType || assignedVehicleLoadType || null,
          vehicleLabel: d.vehicleLabel || assignedVehicleLabel || null,
        });
      } else {
        // 2) fall back to prestart for meta fields
        const preRef = doc(db, "prestarts", prestartDocId || id);
        const preSnap = await getDoc(preRef);
        if (preSnap.exists()) {
          const p = preSnap.data();
          setMeta({
            companyId: p.companyId || assignedCompanyId || null,
            companyName: p.companyName || assignedCompanyName || null,
            truckId: p.truckId || p.vehicleId || assignedVehicleId || null,
            registrationNo: p.registrationNo || p.vehicleRego || assignedVehicleRego || null,
            unitType: p.unitType || p.vehicleLoadType || assignedVehicleLoadType || null,
            vehicleLabel: p.vehicleLabel || assignedVehicleLabel || null,
          });
        }
      }
    };

    load();
  }, [
    driverId,
    date,
    postTripDocId,
    prestartDocId,
    assignedCompanyId,
    assignedCompanyName,
    assignedVehicleId,
    assignedVehicleRego,
    assignedVehicleLoadType,
    assignedVehicleLabel,
  ]);

  const save = async () => {
    if (!odoEnd || Number.isNaN(Number(odoEnd))) {
      alert("Please enter a numeric Odometer End.");
      return;
    }
    if (!hourMeterEnd || Number.isNaN(Number(hourMeterEnd))) {
      alert("Please enter a numeric Hour Meter End.");
      return;
    }
    if (!String(meta.registrationNo || assignedVehicleRego || "").trim()) {
      alert("Please enter the vehicle registration number.");
      return;
    }
    if (!tripEndTime) {
      alert("Please set Trip End Time.");
      return;
    }
    if (!sig) {
      alert("Please sign before saving.");
      return;
    }

    const completedAt = new Date().toLocaleString("en-AU", {
      timeZone: "Australia/Melbourne",
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    });

    const payload = {
      driverId,
      date,
      completedAt,
      runKey: runKey || null,
      odometerEnd: Number(odoEnd),
      hourMeterEnd: Number(hourMeterEnd),
      tripEndTime,
      notes: notes || "",
      signature: sig,
      companyId: meta.companyId || null,
      companyName: meta.companyName || null,
      truckId: meta.truckId || null,
      vehicleId: meta.truckId || null,
      registrationNo: meta.registrationNo || assignedVehicleRego || null,
      unitType: meta.unitType || assignedVehicleLoadType || null,
      vehicleRego: meta.registrationNo || assignedVehicleRego || null,
      vehicleLoadType: meta.unitType || assignedVehicleLoadType || null,
      vehicleLabel:
        meta.vehicleLabel ||
        assignedVehicleLabel ||
        [meta.registrationNo || assignedVehicleRego, meta.unitType || assignedVehicleLoadType]
          .filter(Boolean)
          .join(" • ") ||
        null,
      driverEmail: driverEmail || null,
    };

    const id = postTripDocId || prestartDocId || `${driverId}_${date}`;
    await setDoc(doc(db, "postTrips", id), payload, { merge: true });
    onCompleted?.(payload);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-[95%] max-w-3xl p-5 space-y-5 overflow-y-auto max-h-[90vh]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold">Post Trip Check</h3>
            {(runKey || meta.vehicleLabel || meta.registrationNo) && (
              <div className="mt-1 text-sm text-gray-600">
                {runKey ? <div>Run: <span className="font-medium text-gray-900">{runKey}</span></div> : null}
                <div>
                  Vehicle:{" "}
                  <span className="font-medium text-gray-900">
                    {meta.vehicleLabel ||
                      [meta.registrationNo || assignedVehicleRego, meta.unitType || assignedVehicleLoadType]
                        .filter(Boolean)
                        .join(" • ") ||
                      "Not recorded"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="px-3 py-1 rounded border">
            Close
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <label className="flex flex-col">
            Unit Type
            <input
              className="border rounded px-2 py-1"
              value={meta.unitType || ""}
              onChange={(e) => setMeta((prev) => ({ ...prev, unitType: e.target.value }))}
            />
          </label>
          <label className="flex flex-col">
            Registration No. *
            <input
              className="border rounded px-2 py-1"
              value={meta.registrationNo || ""}
              onChange={(e) => setMeta((prev) => ({ ...prev, registrationNo: e.target.value }))}
            />
          </label>
          <label className="flex flex-col">
            Company
            <input
              className="border rounded px-2 py-1"
              value={meta.companyName || meta.companyId || ""}
              onChange={(e) => setMeta((prev) => ({ ...prev, companyName: e.target.value }))}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <label className="flex flex-col">
            Odometer End *
            <input
              type="number"
              className="border rounded px-2 py-1"
              value={odoEnd}
              onChange={(e) => setOdoEnd(e.target.value)}
            />
          </label>
          <label className="flex flex-col">
            Hour Meter End *
            <input
              type="number"
              className="border rounded px-2 py-1"
              value={hourMeterEnd}
              onChange={(e) => setHourMeterEnd(e.target.value)}
            />
          </label>
          <label className="flex flex-col">
            Trip End Time *
            <input
              type="time"
              className="border rounded px-2 py-1"
              value={tripEndTime}
              onChange={(e) => setTripEndTime(e.target.value)}
            />
          </label>
        </div>

        <div className="text-sm">
          <label className="flex flex-col">
            Notes (faults or issues for the truck)
            <textarea
              className="border rounded px-2 py-1 min-h-[100px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Record any faults, damages, or issues noticed during the trip."
            />
          </label>
        </div>

        <div className="text-sm">
          <p className="mb-1 font-medium">Driver's Signature *</p>
          <SignaturePad onChange={setSig} />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 rounded border">
            Cancel
          </button>
          <button
            onClick={save}
            className="px-3 py-1 rounded bg-blue-600 text-white"
          >
            Save Post Trip
          </button>
        </div>
      </div>
    </div>
  );
}
